/* eslint-disable @typescript-eslint/no-explicit-any */
import { Close } from "@mui/icons-material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import inputStyles from "../../assets/styles/components/input.module.css";
import containerModal from "../../assets/styles/components/containerModal.module.css";
import modal from "../../assets/styles/components/modal.module.css";
import styles from "./DocumentHistoryModal.module.css";

import Button from "../Button/Button";
import SelectDocumentType from "./SelectDocumentType";

// Novos imports de ícones e utils
import DeleteIcon from "@mui/icons-material/Delete";
import DescriptionIcon from "@mui/icons-material/Description";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { handleDownloadDocument, handleViewDocument } from "./DocumentUtils"; // Ajuste o caminho se necessário

import { toast } from "react-toastify";
import {
  getDocumentFile,
  getDocumentHistory,
  softDeleteDocument,
  updateDocument,
} from "../../services/documentsManager";

interface DocumentHistoryModalProps {
  onClose: () => void;
  entidade:
    | "FINANCPAG"
    | "FINANCREC"
    | "EDUALUNO"
    | "RHCOLLAB"
    | "COMPRAS"
    | "FORNECEDOR"
    | "CUSTOMERS";
  uid_vinculo: string;
  initialDocType?: string;
}

const DocumentHistoryModal = ({
  onClose,
  entidade,
  uid_vinculo,
  initialDocType,
}: DocumentHistoryModalProps) => {
  const queryClient = useQueryClient();

  const [selectedType, setSelectedType] = useState<string>(
    initialDocType || "",
  );
  const [, setUidEntidade] = useState<string>("");

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, []);

  const { data: historyList, isLoading } = useQuery({
    queryKey: ["getDocumentHistory", uid_vinculo, selectedType],
    queryFn: () => getDocumentHistory(uid_vinculo, selectedType),
    enabled: !!selectedType,
  });

  const updateMutation = useMutation({
    mutationFn: async (data: any) => {
      return await updateDocument(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["getDocumentHistory", uid_vinculo, selectedType],
      });
      toast.success("Campo atualizado com sucesso!");
    },
    onError: (error: any) => {
      toast.error(error?.message || "Erro ao atualizar documento");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (uid_documento: string) => {
      const confirmed = window.confirm(
        "Deseja realmente excluir este registro de histórico?",
      );
      if (confirmed) {
        return await softDeleteDocument(uid_documento);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["getDocumentHistory", uid_vinculo, selectedType],
      });
      toast.success("Registro excluído com sucesso!");
    },
    onError: (error: any) => {
      toast.error(error?.message || "Erro ao excluir");
    },
  });

  const handleUpdate = (item: any, field: string, newValue: string) => {
    if (item[field] !== newValue) {
      updateMutation.mutate({
        uid_documento: item.uid_documento,
        [field]: newValue,
      });
    }
  };

  return (
    <div className={modal.containerModal}>
      <div
        className={`${containerModal.containerModal} ${styles.historyModal}`}
      >
        <h1>Histórico de envios</h1>

        <div className={containerModal.close}>
          <Button variant="contain" typeStyle={["small", "cancel"]} onClick={onClose} type="button">
            <Close />
          </Button>
        </div>

        <div className={styles.row}>
          <div className={styles.fieldGroup} style={{ flex: 1 }}>
            <SelectDocumentType
              entidade={entidade}
              value={selectedType}
              handleChangeDocumentType={setSelectedType}
              handleEntityFound={setUidEntidade}
            />
          </div>
        </div>

        <div className={styles.containerTable}>
          <table>
            <thead>
              <tr>
                <th className={styles.colStatus}>Status</th>
                <th className={styles.colDesc}>Descrição</th>
                <th className={styles.colTitle}>Título</th>
                <th className={styles.colDate}>Vigência Início</th>
                <th className={styles.colDate}>Vigência Fim</th>
                <th className={styles.colAction}>Ação</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={6} className={styles.loadingCell}>
                    Carregando histórico...
                  </td>
                </tr>
              ) : !selectedType ? (
                <tr>
                  <td colSpan={6} className={styles.noContent}>
                    Selecione um tipo de documento para ver o histórico.
                  </td>
                </tr>
              ) : historyList && historyList.length > 0 ? (
                historyList.map((doc: any) => (
                  <tr
                    key={doc.uid_documento}
                    className={doc.vigente ? styles.activeRow : ""}
                  >
                    <td className={styles.colStatus}>
                      <div
                        className={`
                                                                ${styles.iconBase} 
                                                                ${
                                                                  doc.vigente
                                                                    ? styles.iconActive
                                                                    : styles.iconInactive
                                                                }
                                                              `}
                        title={
                          doc.vigente
                            ? "Documento Vigente"
                            : "Versão Anterior (Histórico)"
                        }
                      >
                        <DescriptionIcon />
                      </div>
                    </td>

                    <td className={styles.colDesc}>
                      <input
                        className={`${inputStyles.input} ${styles.tableInputOverride}`}
                        defaultValue={doc.descricao}
                        onBlur={(e) =>
                          handleUpdate(doc, "descricao", e.target.value)
                        }
                        placeholder="Descrição..."
                      />
                    </td>

                    <td className={styles.colTitle}>
                      <input
                        className={`${inputStyles.input} ${styles.tableInputOverride}`}
                        defaultValue={doc.titulo}
                        onBlur={(e) =>
                          handleUpdate(doc, "titulo", e.target.value)
                        }
                      />
                    </td>

                    <td className={styles.colDate}>
                      <input
                        type="date"
                        className={`${inputStyles.input} ${styles.tableInputOverride}`}
                        defaultValue={
                          doc.inicio ? doc.inicio.split("T")[0] : ""
                        }
                        onBlur={(e) =>
                          handleUpdate(doc, "inicio", e.target.value)
                        }
                      />
                    </td>

                    <td className={styles.colDate}>
                      <input
                        type="date"
                        className={`${inputStyles.input} ${styles.tableInputOverride}`}
                        defaultValue={doc.fim ? doc.fim.split("T")[0] : ""}
                        onBlur={(e) => handleUpdate(doc, "fim", e.target.value)}
                      />
                    </td>

                    <td className={styles.colAction}>
                      <div
                        style={{
                          display: "flex",
                          gap: "5px",
                          justifyContent: "center",
                        }}
                      >
                        <Button
                          variant="textButton"
                          title="Visualizar documento"
                          onClick={() =>
                            handleViewDocument(
                              doc.uid_documento,
                              getDocumentFile,
                            )
                          }
                          type="button"
                        >
                          <VisibilityIcon />
                        </Button>

                        <Button
                          variant="textButton"
                          title="Baixar documento"
                          onClick={() =>
                            handleDownloadDocument(
                              doc.uid_documento,
                              getDocumentFile,
                            )
                          }
                          type="button"
                        >
                          <FileDownloadIcon />
                        </Button>

                        <Button
                          variant="textButton"
                          title="Excluir registro"
                          onClick={() =>
                            deleteMutation.mutate(doc.uid_documento)
                          }
                          disabled={doc.vigente}
                        >
                          <DeleteIcon />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className={styles.noContent}>
                    Nenhum histórico encontrado para este tipo.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default DocumentHistoryModal;
